import { create } from 'zustand';
import {
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    fetchUnreadCount,
    Notification
} from './actions';

interface NotificationState {
    notifications: Notification[];
    unreadCount: number;
    loading: boolean;
    error: string | null;
    lastPolled: number;

    // Actions
    getNotifications: () => Promise<void>;
    getUnreadCount: () => Promise<void>;
    markRead: (id: string) => Promise<void>;
    markAllRead: () => Promise<void>;

    // Polling management
    startPolling: (companyId?: string) => void;
    stopPolling: () => void;
    _pollTimer: NodeJS.Timeout | null;
}

export const useNotifications = create<NotificationState>((set, getState) => ({
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
    lastPolled: 0,
    _pollTimer: null,

    getNotifications: async () => {
        set({ loading: true, error: null });
        try {
            const res = await fetchNotifications();
            if (res.Code === "200") {
                set({
                    notifications: res.Msg.notifications,
                    loading: false,
                    lastPolled: Date.now()
                });
            } else {
                set({ error: "Failed to fetch notifications", loading: false });
            }
        } catch (err: any) {
            set({ error: err.message, loading: false });
        }
    },

    getUnreadCount: async () => {
        try {
            const res = await fetchUnreadCount();
            if (res.Code === "200") {
                set({ unreadCount: res.Msg.count });
            }
        } catch (err) {
            console.error("Failed to fetch unread count", err);
        }
    },

    markRead: async (id: string) => {
        try {
            const res = await markAsRead(id);
            if (res.Code === "200") {
                // Optimistic update
                set((state) => ({
                    notifications: state.notifications.map(n =>
                        n.id === id ? { ...n, read: true } : n
                    ),
                    unreadCount: Math.max(0, state.unreadCount - 1)
                }));
            }
        } catch (err) {
            console.error("Failed to mark as read", id, err);
        }
    },

    markAllRead: async () => {
        try {
            const res = await markAllAsRead();
            if (res.Code === "200") {
                set((state) => ({
                    notifications: state.notifications.map(n => ({ ...n, read: true })),
                    unreadCount: 0
                }));
            }
        } catch (err) {
            console.error("Failed to mark all as read", err);
        }
    },

    startPolling: () => {
        const { stopPolling, getNotifications, getUnreadCount } = getState();
        stopPolling();

        // Initial fetch
        getNotifications();
        getUnreadCount();

        // Polling every 30 seconds
        const timer = setInterval(() => {
            // Only poll if tab is active
            if (document.visibilityState === 'visible') {
                getNotifications();
                getUnreadCount();
            }
        }, 30000);

        set({ _pollTimer: timer });

        // Visibility Change Listener
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // Immediate fetch when tab becomes visible again
                getNotifications();
                getUnreadCount();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
    },

    stopPolling: () => {
        const { _pollTimer } = getState();
        if (_pollTimer) {
            clearInterval(_pollTimer);
            set({ _pollTimer: null });
        }
    }
}));
